import { logger } from "@/lib/logger"

import { getErrorResponse } from "./errorUtils"
import { httpClient } from "./httpClient"
import type {
  FavoriteMeeting,
  MeetingEndScreenResponse,
  MeetingParticipationHistoryItem,
  MeetingProfile,
  MeetingProfileParticipant,
  MeetingSessionDetails,
  MeetingSummaryActionItem,
  MeetingSummaryNote,
  MeetingSummaryStatusResponse,
  MeetingSummaryTranscriptionRow,
  MeetingSummaryView,
  RoomHistoryItem,
  SummaryEditRequest,
} from "./types"
import {
  formatAvatarUrl,
  formatMeeting,
  formatMeetings,
  formatRoomHistoryItems,
  toUnixMilliseconds,
} from "./utils"

const formatMeetingParticipant = <T extends MeetingProfileParticipant>(
  participant: T,
): T => ({
  ...participant,
  avatar: formatAvatarUrl(participant.avatar),
  // HACK
  authorized: participant.id.length < 36,
})

const formatMeetingSummaryActionItem = (
  actionItem: MeetingSummaryActionItem,
): MeetingSummaryActionItem => ({
  ...actionItem,
  avatar: actionItem.avatar ? formatAvatarUrl(actionItem.avatar) : "",
})

const formatMeetingSummaryTranscriptionRow = (
  row: MeetingSummaryTranscriptionRow,
): MeetingSummaryTranscriptionRow => ({
  ...row,
  avatar: row.avatar ? formatAvatarUrl(row.avatar) : "",
  unixtime: toUnixMilliseconds(row.unixtime) ?? 0,
})

const formatMeetingSummaryNote = (
  note: MeetingSummaryNote,
): MeetingSummaryNote => ({
  ...note,
  unixtime: toUnixMilliseconds(note.unixtime) ?? 0,
})

const formatMeetingSummary = (
  summary: MeetingSummaryView,
): MeetingSummaryView => ({
  ...summary,
  fullTranscription:
    summary.fullTranscription?.map(formatMeetingSummaryTranscriptionRow) ??
    null,
  notes: summary.notes?.map(formatMeetingSummaryNote) ?? null,
  actionItems: summary.actionItems?.map(formatMeetingSummaryActionItem) ?? null,
})

const formatMeetingProfile = (profile: MeetingProfile): MeetingProfile => ({
  ...formatMeeting(profile),
  participants: profile.participants.map(formatMeetingParticipant),
})

const formatMeetingSessionDetails = (
  session: MeetingSessionDetails,
): MeetingSessionDetails => ({
  ...session,
  participants: session.participants.map(formatMeetingParticipant),
  summary: session.summary ? formatMeetingSummary(session.summary) : undefined,
  recordings: session.recordings ?? [],
})

const formatMeetingEndScreen = (
  endScreen: MeetingEndScreenResponse,
): MeetingEndScreenResponse => ({
  ...endScreen,
  summary: endScreen.summary
    ? formatMeetingSummary(endScreen.summary)
    : undefined,
  recordings: endScreen.recordings ?? [],
})

async function getMeetingProfile(request: {
  roomId: string
}): Promise<MeetingProfile> {
  const response = await httpClient.get<MeetingProfile>(
    "/rooms/get-meeting-profile",
    {
      params: request,
    },
  )
  return formatMeetingProfile(response.data)
}

async function getMeetingSessionDetails(request: {
  sessionId: string
}): Promise<MeetingSessionDetails> {
  const response = await httpClient.get<MeetingSessionDetails>(
    "/rooms/get-meeting-session-details",
    {
      params: request,
    },
  )
  return formatMeetingSessionDetails(response.data)
}

async function getMeetingEndScreen(request: {
  roomId?: string
  sessionId?: string
}): Promise<MeetingEndScreenResponse> {
  const response = await httpClient.get<MeetingEndScreenResponse>(
    "/rooms/get-end-screen",
    {
      params: request,
    },
  )

  return formatMeetingEndScreen(response.data)
}

async function getMeetingParticipationHistory(request: {
  roomId: string
  count?: number
  fromTimestamp?: string
}): Promise<MeetingParticipationHistoryItem[] | null> {
  const response = await httpClient.get<
    MeetingParticipationHistoryItem[] | null
  >("/rooms/get-meeting-participation-history", {
    params: request,
  })

  return Array.isArray(response.data) ? response.data : null
}

async function enableSummary(request: {
  roomId: string
}): Promise<MeetingSummaryStatusResponse> {
  const response = await httpClient.post<MeetingSummaryStatusResponse>(
    "/rooms/summary/enable",
    request,
  )
  return response.data
}

async function disableSummary(request: {
  roomId: string
}): Promise<MeetingSummaryStatusResponse> {
  const response = await httpClient.post<MeetingSummaryStatusResponse>(
    "/rooms/summary/disable",
    request,
  )
  return response.data
}

async function getSummaryStatus(request: {
  roomId: string
}): Promise<MeetingSummaryStatusResponse> {
  const response = await httpClient.get<MeetingSummaryStatusResponse>(
    "/rooms/summary/status",
    {
      params: request,
    },
  )
  return response.data
}

async function setSummaryConfirmed(request: {
  sessionId: string
}): Promise<MeetingSessionDetails> {
  const response = await httpClient.post<MeetingSessionDetails>(
    "/rooms/summary/set-confirmed",
    request,
  )
  return formatMeetingSessionDetails(response.data)
}

async function editSummary(
  request: SummaryEditRequest,
): Promise<MeetingSessionDetails> {
  const response = await httpClient.post<MeetingSessionDetails>(
    "/rooms/summary/edit",
    request,
  )
  return formatMeetingSessionDetails(response.data)
}

async function getSummaryPdf(request: {
  sessionId: string
  timezone?: string
}): Promise<Blob> {
  try {
    const response = await httpClient.get<Blob>("/rooms/summary/pdf", {
      params: request,
      responseType: "blob",
    })

    return response.data
  } catch (error) {
    // Axios keeps error responses as Blob when the successful response is a PDF.
    // Decode that body so session-scoped callers can still distinguish its code.
    const response = getErrorResponse(error)
    if (typeof Blob === "undefined" || !(response?.data instanceof Blob)) {
      throw error
    }

    const text = await response.data.text()
    let data: unknown = text
    try {
      data = JSON.parse(text) as unknown
    } catch {
      // The API may return a plain-text error code.
    }

    if (typeof error === "object" && error !== null) {
      throw Object.assign(error, {
        response: { ...response, data },
      })
    }

    throw error
  }
}

async function getWorkspaceMeetingProfiles(
  workspaceId: string,
): Promise<MeetingProfile[]> {
  logger.log("meeting api: getWorkspaceMeetingProfiles", { workspaceId })
  const response = await httpClient.get<MeetingProfile[]>(
    `/workspace/${workspaceId}/meetings/profiles`,
  )
  return response.data.map(formatMeetingProfile)
}

async function getWorkspaceFavoriteMeetings(
  workspaceId: string,
): Promise<FavoriteMeeting[]> {
  logger.log("meeting api: getWorkspaceFavoriteMeetings", { workspaceId })
  const response = await httpClient.get<FavoriteMeeting[]>(
    `/workspace/${workspaceId}/meetings/favorites`,
  )
  return formatMeetings(response.data)
}

async function getWorkspaceMeetingHistory(
  workspaceId: string,
  options: { count?: number } = {},
): Promise<RoomHistoryItem[]> {
  const count = options.count ?? 30
  logger.log("meeting api: getWorkspaceMeetingHistory", { workspaceId, count })
  const response = await httpClient.get<RoomHistoryItem[]>(
    `/workspace/${workspaceId}/meetings/history`,
    { params: { count } },
  )
  return formatRoomHistoryItems(response.data)
}

async function hideWorkspaceMeetingHistory(
  workspaceId: string,
  roomSessionId: string,
): Promise<string> {
  logger.log("meeting api: hideWorkspaceMeetingHistory", {
    workspaceId,
    roomSessionId,
  })
  const response = await httpClient.post<string>(
    `/workspace/${workspaceId}/meetings/history/${encodeURIComponent(roomSessionId)}/hide`,
  )
  return response.data
}

async function restoreWorkspaceMeetingHistory(
  workspaceId: string,
  roomSessionId: string,
): Promise<string> {
  logger.log("meeting api: restoreWorkspaceMeetingHistory", {
    workspaceId,
    roomSessionId,
  })
  const response = await httpClient.delete<string>(
    `/workspace/${workspaceId}/meetings/history/${encodeURIComponent(roomSessionId)}/hide`,
  )
  return response.data
}

async function removeWorkspaceMeetingHistory(
  workspaceId: string,
  roomSessionId: string,
): Promise<string> {
  logger.log("meeting api: removeWorkspaceMeetingHistory", {
    workspaceId,
    roomSessionId,
  })
  const response = await httpClient.delete<string>(
    `/workspace/${workspaceId}/meetings/history/${encodeURIComponent(roomSessionId)}`,
  )
  return response.data
}

export const meetingApi = {
  getMeetingProfile,
  getMeetingParticipationHistory,
  getMeetingSessionDetails,
  getMeetingEndScreen,
  enableSummary,
  disableSummary,
  getSummaryStatus,
  setSummaryConfirmed,
  editSummary,
  getSummaryPdf,
  getWorkspaceMeetingProfiles,
  getWorkspaceFavoriteMeetings,
  getWorkspaceMeetingHistory,
  hideWorkspaceMeetingHistory,
  restoreWorkspaceMeetingHistory,
  removeWorkspaceMeetingHistory,
}
